import { isApprovedOrigin } from '@/lib/trustedOrigins';

const isNode = typeof window === 'undefined';
const storage = isNode ? new Map() : window.localStorage;

// ── Trusted deployment configuration ────────────────────────────────────────
// App ID, backend base URL and functions version come ONLY from build-time
// deployment configuration. URL parameters and browser storage are untrusted
// input: a crafted link must never repoint this client at another app, another
// backend, or another functions version.
const DEPLOYED_APP_ID = import.meta.env.VITE_BASE44_APP_ID;
const DEPLOYED_APP_BASE_URL = import.meta.env.VITE_BASE44_APP_BASE_URL;
const DEPLOYED_FUNCTIONS_VERSION = import.meta.env.VITE_BASE44_FUNCTIONS_VERSION;

// Older builds honoured ?app_id / ?app_base_url / ?functions_version and kept
// them in storage. Remove them so a previously poisoned browser cannot keep
// sending traffic to the wrong backend.
const LEGACY_OVERRIDE_KEYS = [
	'base44_app_id',
	'base44_app_base_url',
	'base44_functions_version',
];

if (!isNode) {
	for (const key of LEGACY_OVERRIDE_KEYS) {
		try { storage.removeItem(key); } catch { /* storage unavailable */ }
	}
}

const readUrlParam = (name) => {
	if (isNode) return null;
	try {
		return new URLSearchParams(window.location.search).get(name);
	} catch {
		return null;
	}
};

const stripUrlParams = (names) => {
	if (isNode) return;
	try {
		const urlParams = new URLSearchParams(window.location.search);
		names.forEach((name) => urlParams.delete(name));
		const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`;
		window.history.replaceState({}, document.title, newUrl);
	} catch { /* history unavailable */ }
};

const getAppParams = () => {
	if (readUrlParam('clear_access_token') === 'true') {
		storage.removeItem('base44_access_token');
		storage.removeItem('token');
	}

	// The callback credential the Base44 login flow appends is the only URL
	// value we accept, and only when this browser has no session yet — it can
	// never replace an existing session (session fixation).
	let token = storage.getItem('base44_access_token') || storage.getItem('token') || null;
	const urlToken = readUrlParam('access_token');
	if (urlToken) {
		if (!token && /^[A-Za-z0-9._~+/=-]{16,4096}$/.test(urlToken)) token = urlToken;
		stripUrlParams(['access_token']);
	}

	// A return target supplied in the URL is validated against the approved
	// hosts before it is used for anything.
	let fromUrl = readUrlParam('from_url');
	if (fromUrl && !isApprovedOrigin(fromUrl)) fromUrl = null;
	if (readUrlParam('from_url')) stripUrlParams(['from_url']);

	return {
		appId: DEPLOYED_APP_ID,
		token,
		fromUrl: fromUrl || (isNode ? undefined : window.location.href),
		functionsVersion: DEPLOYED_FUNCTIONS_VERSION,
		appBaseUrl: DEPLOYED_APP_BASE_URL,
	};
};

export const appParams = {
	...getAppParams()
};